import { Injectable } from '@angular/core';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { filter, Observable } from 'rxjs';
import { VehicleService } from './vehicle.service';
import { AddressModalComponent, AddressModalContext } from '../../address/address-modal.component';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';
import { VehicleAddressModel } from '../models/vehicle-address.model';
import { OwnershipService } from '../../titleregistration/services/ownership.service';
import { VehicleOwnershipModel } from '../../titleregistration/models/vehicle-ownership.model';
import { Message } from 'primeng/api';
import { RedactionService } from '../../shared/services/redaction/redaction.service';
import { CustomerCharacteristic } from '../../customer/models/customer-characteristic.model';
import { OwnershipCustomerRelationshipStatusType } from '../../titleregistration/models/enums/ownership-customer-relationship-status-type.enum';

@Injectable({
    providedIn: 'root'
})
export class VehicleDomicileService extends BaseApiService {

    private ownershipCustomerRelationshipStatusTypes: OwnershipCustomerRelationshipStatusType = new OwnershipCustomerRelationshipStatusType();

    public constructor(private vehicleService: VehicleService
        , public modalService: MvdModalService
        , public ownershipService: OwnershipService
        , public redactionService: RedactionService
    ) {
        super();
    }

    public addDomicile(vehicleOwnershipModel: VehicleOwnershipModel, growlMessages: Message[], updateAction: (s: string) => void) {
        const data = new AddressModalContext(0, 0, 'domicile', true);
        this.modalService.open(AddressModalComponent, data, ModalSize.Medium).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(address => {
                const domAddress = this.copyVehicleAddress(new VehicleAddressModel(), address, vehicleOwnershipModel);

                this.ownershipService.updateVehicleAddress(domAddress).subscribe(
                    async r => {
                        const successMessage = 'Domicile Saved successfully.';
                        updateAction(successMessage);
                    },
                    err => {
                        growlMessages.push({ severity: 'error', summary: 'Error', detail: 'An error occured. Please try again.' });
                    }
                );

            });
    }

    /**
     * Checks which personal identifiable information must be redacted
     **/
    private isResidentialAddressRedactionNecessary(vehicleOwnershipModel: VehicleOwnershipModel): boolean {
        const allCustomerCharacteristics = this.getAllCharacteristics(vehicleOwnershipModel);
        return this.redactionService.isResidentialAddressRedactionNecessary(allCustomerCharacteristics);
    }

    private getAllCharacteristics(vehicleOwnershipModel: VehicleOwnershipModel): CustomerCharacteristic[] {
        let characteristics = [];
        if (!vehicleOwnershipModel || !vehicleOwnershipModel.ownershipCustomerRelationships) {
            return characteristics;
        }

        const characteristicSets = vehicleOwnershipModel.ownershipCustomerRelationships.map(o => {
            const isActive = o.ownershipCustomerRelationshipStatusTypeId == this.ownershipCustomerRelationshipStatusTypes.Active.id;

            return isActive && o.customer && o.customer.characteristics ? o.customer.characteristics : [];
        });

        characteristics = characteristicSets.reduce((characteristics, characteristicSet) => characteristics.concat(characteristicSet));

        return characteristics;
    }

    public editDomicile(vehicleOwnershipModel: VehicleOwnershipModel, growlMessages: Message[], updateAction: (s: string) => void): void {
        const self = this;

        let isResidentialAddressRedactionNecessary = this.isResidentialAddressRedactionNecessary(vehicleOwnershipModel);
        // ToDo - look into passing isResidentialAddressRedactionNecessary
        this.vehicleService.getInProcesssBusinessTransactionWipStatuses(vehicleOwnershipModel.vehicleID).subscribe(r => {
            var vehicleAddress = vehicleOwnershipModel.vehicleAddress;
            const data = new AddressModalContext(vehicleAddress.addressNumberId, vehicleAddress.id, 'domicile', false, false, isResidentialAddressRedactionNecessary);
            if (r.length > 0) {
                const message = 'Services have already been started for this vehicle. If you modify the domicile now, any Services completed after this will overwrite your changes. Are you sure you want to modify the domicile address?';

                this.modalService.confirm().title('Edit Domicile').body(message)
                    .okBtn('Yes').cancelBtn('No')
                    .open().afterClosed()
                    .pipe(filter(result => !!result))
                    .subscribe(() => {
                        this.modalService.open(AddressModalComponent, data, ModalSize.Medium).afterClosed()
                            .pipe(filter(result => !!result))
                            .subscribe(address => this.updateVehicleAddress(address, vehicleOwnershipModel, growlMessages, updateAction));
                    });
            } else {
                this.modalService.open(AddressModalComponent, data, ModalSize.Medium).afterClosed()
                    .pipe(filter(result => !!result))
                    .subscribe(address => this.updateVehicleAddress(address, vehicleOwnershipModel, growlMessages, updateAction));
            }

        }, e => growlMessages.push({ severity: 'error', summary: 'Error', detail: 'An error occured. Please try again.' }));

    }

    public deleteDomicile(vehicleOwnershipModel: VehicleOwnershipModel, growlMessages: Message[], updateAction: () => void) {
        const self = this;

        this.vehicleService.getInProcesssBusinessTransactionWipStatuses(vehicleOwnershipModel.vehicleID).subscribe(r => {
            let message = 'Are you sure you want to remove the domicile address?';
            if (r.length > 0) {
                message = 'Services have already been started for this vehicle. Removing the domicile will require those Services to be deleted. Are you sure you want to remove the domicile address?';
            }

            this.modalService.confirm().title('Delete Domicile').body(message)
                .okBtn('Yes').cancelBtn('No')
                .open().afterClosed()
                .pipe(filter(result => !!result))
                .subscribe(() => {
                    var vehicleAddress = vehicleOwnershipModel.vehicleAddress;
                    vehicleAddress.markedAsDeleted = true;
                    this.ownershipService.updateVehicleAddress(vehicleAddress).subscribe(
                        async r => {
                            growlMessages.push({ severity: 'success', summary: 'Success', detail: 'Deleted successfully.' });
                            updateAction();
                        },
                        err => {
                            growlMessages.push({ severity: 'error', summary: 'Error', detail: 'An error occured. Please try again.' });
                        }
                    );
                });
        }, e => growlMessages.push({ severity: 'error', summary: 'Error', detail: 'An error occured. Please try again.' }));

    }

    private updateVehicleAddress(address: any, vehicleOwnershipModel: VehicleOwnershipModel, growlMessages: Message[], updateAction: (s: string) => void): void {
        let updatedAddress = Object.assign(new VehicleAddressModel(), vehicleOwnershipModel.vehicleAddress);
        updatedAddress = this.copyVehicleAddress(updatedAddress, address, vehicleOwnershipModel);
        this.ownershipService.updateVehicleAddress(updatedAddress).subscribe(
            async (r) => {
                /// / Getting the new address through the back end ensures that it will be redacted if necessary.
                const successMessage = 'Vehicle Domicile successfully.';
                updateAction(successMessage);
                // this.getVehicleOwnership(successMessage);
            },
            err => {
                growlMessages.push({ severity: 'error', summary: 'Error', detail: 'An error occured. Please try again.' });
            }
        );
    }

    private copyVehicleAddress(target: VehicleAddressModel, source: any, vehicleOwnershipModel: VehicleOwnershipModel): VehicleAddressModel {
        target.addressLine1 = source.addressLine1;
        target.addressLine2 = source.addressLine2;
        target.countyId = source.countyId;
        target.countryId = source.countryId;
        target.inCareOf = source.inCareOf;
        target.postalDeliveryCode = source.postalDeliveryCode;
        target.postalZipCode = source.postalZipCode;
        target.city = source.city;
        target.isUndeliverable = source.isUndeliverable;
        target.jurisdictionId = source.jurisdictionId;
        target.latitude = source.latitude;
        target.longtitude = source.longitude;
        target.addressTypeId = source.addressTypeId;
        target.vehicleOwnershipId = vehicleOwnershipModel.id;
        target.emissionAreaTypeId = source.emissionAreaTypeId;

        return target;
    }
}
